import { useMemo } from 'react';
import type { FlowLayoutTable } from '@shared/flow/flow-layouts';
import type { FlowView, SavedViewWire } from '@shared/flow/view';
import type { FlowViewMachine } from '../views/useViewStateMachine';
import type { FlowListLayoutResult } from '../../../hooks/useFlowLayouts';
import {
  beginFlowLayoutPinWrite,
  endFlowLayoutPinWrite,
  setWorkingFlowLayout,
  useFlowLayoutWorkingStore,
  workingFlowLayoutVersion,
} from './flow-layout-working-store';

/**
 * Wrap a table's `FlowViewMachine` so an unsaved layout pick reads as
 * "Modified" — without touching `useViewStateMachine`, `ViewControl`,
 * `useViewActions` or `SavedViewsPane`, which all keep reading the ordinary
 * machine shape.
 *
 * - `isDirty` is widened: dirty when the machine is, OR a working layout is
 *   held for `table` in the session-only working store.
 * - `reset` (Reset to last save) drops the working layout.
 * - `save` and `saveAsNew` PIN the working layout on the view (KTD-6): the
 *   pick is passed as `{ flowLayout }` to the machine, which writes it on the
 *   row. The working pick is cleared only when the machine hands back a saved
 *   row; a `null` row (the machine toasted and gave up, or refused a preset /
 *   teammate's view) keeps it, so the view stays Modified and the next Save
 *   retries. A rejection propagates unchanged and clears nothing.
 * - `saveCopyOf` delegates unchanged — the copy carries the SOURCE view's own
 *   pin — and drops the working pick on a created row.
 * - `applyCreated` (a view created elsewhere, e.g. Browse All) delegates and
 *   drops the working pick, consistent with `openView`.
 * - `openView` drops the working layout BEFORE delegating — switching views
 *   discards an uncommitted pick rather than carrying it onto the next view.
 * - No path here writes the rep's per-table saved layout choice: "Set as
 *   default layout" (`useFlowListLayout().setAsDefault`) is its only writer,
 *   so a view author's pin never silently becomes a rep's default.
 *
 * Pin-writing saves are bracketed (`beginFlowLayoutPinWrite` /
 * `endFlowLayoutPinWrite`, in a `finally`): the machine installs the saved
 * row's pin as the view pin BEFORE `save` returns, so a rep who picks the
 * baseline layout mid-flight would otherwise collapse to "no working pick" and
 * lose to the incoming pin. While the bracket is open `pick` keeps a
 * baseline-equal pick explicit; after the save lands the layout reads
 * Modified against the new pin. `saveCopyOf` writes no pin and is not bracketed.
 *
 * Every path captures the pick AND the table's working-store version when the
 * call starts. If the rep picked again while the save was in flight (any pick,
 * even back to the baseline), nothing is cleared, so the newer pick stands;
 * the pin already sent is the pick as of Save.
 *
 * Accepted gaps:
 * - `copyLink` on a dirty own view checks the RAW machine `isDirty`, so a
 *   layout-only Modified never triggers it. If both are dirty, the new view
 *   opens with the pick still working (Modified), not lost.
 * - `deleteView` of the active view leaves the pick working.
 * - The machine's own `?view=` / first-resolution effect opens views without
 *   going through this wrapper; the working store is session-only and
 *   table-scoped, so the pick survives it.
 * - A store entry equal to the layout's baseline is inert: it is not dirty
 *   (the read is baseline-aware, `layout.isModified`). Such an entry can only be left behind
 *   by the pin-write bracket above; it lingers until the next pick, Reset,
 *   openView or save on the table. Bounded and accepted. If a save does run
 *   with one, it is pinned explicitly (the same layout the view already shows),
 *   because the pin sent must not depend on which baseline a retained machine
 *   callback inherited.
 */
export function useFlowLayoutAwareMachine(
  machine: FlowViewMachine,
  table: FlowLayoutTable,
  layout: Pick<FlowListLayoutResult, 'isModified'>,
): FlowViewMachine {
  // The table's pick version as of THIS render: the baseline a retained
  // `applyCreated` compares against when it finally runs.
  const renderVersion = useFlowLayoutWorkingStore((s) => s.versionBySlot[table] ?? 0);
  return useMemo(() => {
    // The pick and its version, both read from the live store in one go at the
    // moment a save path starts, never a render-closure pick paired with a
    // live version. The rep may choose again while the save is in flight; the
    // version tells.
    const capture = () => {
      const { bySlot, versionBySlot } = useFlowLayoutWorkingStore.getState();
      const live = bySlot[table] ?? null;
      // The pick exactly as the rep sees it chosen, never normalised against a
      // baseline: a retained machine callback (e.g. the "Save as new" dialog
      // opened mid-save) inherits ITS OLD pin when sent no options, so the pin
      // we send must be explicit whenever a pick exists. No pick, no pin.
      return { pick: live, version: versionBySlot[table] ?? 0 };
    };
    const unchanged = (since: number) => workingFlowLayoutVersion(table) === since;
    return {
      ...machine,
      isDirty: machine.isDirty || layout.isModified,
      reset: () => {
        machine.reset();
        setWorkingFlowLayout(table, null);
      },
      save: async () => {
        const { pick, version } = capture();
        const pin = pick;
        if (pin) beginFlowLayoutPinWrite(table);
        let row: SavedViewWire | null;
        try {
          row = await machine.save(pin ? { flowLayout: pin } : undefined);
        } finally {
          if (pin) endFlowLayoutPinWrite(table);
        }
        // The rep chose again during the save: their newer pick stands.
        if (!unchanged(version)) return row;
        if (row) setWorkingFlowLayout(table, null);
        return row;
      },
      saveAsNew: async (name: string) => {
        const { pick, version } = capture();
        const pin = pick;
        if (pin) beginFlowLayoutPinWrite(table);
        let created: SavedViewWire | null;
        try {
          created = await machine.saveAsNew(name, pin ? { flowLayout: pin } : undefined);
        } finally {
          if (pin) endFlowLayoutPinWrite(table);
        }
        if (!unchanged(version)) return created;
        if (created) setWorkingFlowLayout(table, null);
        return created;
      },
      saveCopyOf: async (view: FlowView, name: string) => {
        const { version } = capture();
        const created = await machine.saveCopyOf(view, name);
        if (created && unchanged(version)) setWorkingFlowLayout(table, null);
        return created;
      },
      openView: async (id: string, opts?: { carry?: boolean }) => {
        setWorkingFlowLayout(table, null);
        return machine.openView(id, opts);
      },
      applyCreated: (created: SavedViewWire) => {
        // Creation ran asynchronously elsewhere (Browse All), so this callback
        // may be older than the rep's latest pick. If the pick moved since the
        // render that made it, only delegate: the newer pick stands.
        const { version } = capture();
        machine.applyCreated(created);
        if (version === renderVersion) setWorkingFlowLayout(table, null);
      },
    };
  }, [machine, layout.isModified, renderVersion, table]);
}
